// ============================================
// Core Data Models for OttoTester
// ============================================

// Variant status indicators
export type VariantStatus = 'production' | 'staging' | 'testing' | 'training' | 'development' | 'custom'

// Field types for business objects
export type FieldType = 'text' | 'email' | 'number' | 'select' | 'toggle' | 'date' | 'textarea'

// Test result status
export type TestStatus = 'pending' | 'running' | 'passed' | 'failed' | 'skipped'

// ============================================
// Application & Variant
// ============================================

export interface ScanResult {
  applicationSummary: string
  scanLimited: boolean
  authRequired: boolean
  authFields: string[]
  /** Off-origin sign-in URLs the scanner observed (a redirect the page
   * followed, or an off-origin "sign in" link), plus URLs a run later
   * confirmed to map to a curated sign-in helper (`source:'planner'`).
   * Variant-scoped. Written by the backend (scan + run-start write-back);
   * surfaced read-only on the Credentials panel. */
  authCandidates?: { url: string; source: 'redirect' | 'href' | 'planner' }[]
  renderingType: string
  framework: string
  /** Short application-domain label (e.g. "trademark search").
   * Per-scan audit trail of what the scanner observed at this URL/time.
   * Application.applicationDomain is the canonical, user-editable copy;
   * scan results auto-promote into empty Application fields only. */
  applicationDomain?: string
  /** 200-500 char usage-context prose.
   * Per-scan audit trail; Application.applicationUsageContext is the
   * canonical, user-editable copy. */
  applicationUsageContext?: string
  scannedAt: Date | string
}

/** Per-variant browser-context overrides. Defaults from configGenerator
 * apply when fields are absent. */
export interface VariantBrowser {
  timezone?: string        // IANA, e.g. 'America/New_York'
  locale?: string          // e.g. 'en-US'
  viewportWidth?: number
  viewportHeight?: number
  /** Milliseconds Playwright pauses between actions (ottotester#233).
   * 0 / undefined → fast path (no delay). ~250–500ms for sites that
   * rate-limit or anti-bot reject rapid-fire automated traffic. */
  slowMotionMs?: number
}

/** Per-credential binding to a pre-canned login fixture. Lives on
 * `TestData.credentials[i].loginFixtureBinding` and is configured from
 * the IdP picker on Test Data (#876 Slice 1). The variant-level
 * binding (`AuthFixtureBinding` + `ResolvedAuthBinding`) was deleted
 * in the same slice — per-credential is canonical. */
export interface LoginFixtureBinding {
  mode: 'library' | 'auto' | 'none'
  /** SystemFixture._id when mode='library'. */
  libraryId?: string
  /** Version of the library entry stamped at bind time so future
   * seeder bumps stay invisible to existing bindings until the user
   * explicitly re-binds. */
  libraryVersion?: number
}

export interface Variant {
  id: string
  applicationId: string
  name: string
  description?: string
  baseUrl: string
  status: VariantStatus
  pathPrefix?: string
  icon?: string
  aiInstructions?: string
  scan?: ScanResult
  browser?: VariantBrowser
  /** Marks this variant as a CI/CD entry point. When true, DELETE
   * requires `?force=true` + `X-OttoTester-Confirm-Delete: <name>`
   * header — the UI surfaces a name-typed confirmation modal. See
   * ADR 0015 § referential integrity and #644. */
  ciProtected: boolean
  /** Present only when the variant has been archived (soft-deleted).
   * Listed in the workspace's Archived tab; restorable within the
   * 30-day window; hard-deleted by the sweep job thereafter. */
  archivedAt?: Date
  createdAt: Date
  updatedAt: Date
}

// ============================================
// Test Plan (with embedded test cases — English specs)
// ============================================

export type TestIntent = 'happy-path' | 'negative' | 'edge-case'
export type PlanCategoryName = 'crud' | 'search' | 'accessibility' | 'readability' | 'performance' | 'navigation' | 'ui'
export type TestCaseStatus = 'planned' | 'scripted' | 'blocked'
export type TestCasePriority = 'P0' | 'P1' | 'P2' | 'P3'

/** Snapshot captured during test case planning */
export interface TestCaseSnapshot {
  stepIndex: number
  url: string
  snapshot: string
  capturedAt: Date | string
}

/**
 * Typed planner assertion clause (#718 slice 1) — the frontend mirror
 * of the server's `TypedAssertion` discriminated union. A planner step's
 * `expect` is an array of these structured clauses, not free text.
 * `target` is an intent-level description, never a CSS selector or
 * locator. `freeform` is the escape hatch + the home for pre-#718
 * migrated prose.
 *
 * `number_saved` and `number_compare` work as a pair: one step
 * saves a number under a name, a later step compares a number with it
 * (or with a fixed number). That is how a test notices a setting that
 * changes nothing, such as a results total that stays the same.
 */
export type TypedAssertion =
  | { kind: 'visible'; target: string; value: boolean }
  | { kind: 'checked'; target: string; value: boolean }
  | { kind: 'enabled'; target: string; value: boolean }
  | { kind: 'text_equals'; target: string; value: string }
  | { kind: 'text_contains'; target: string; value: string }
  | { kind: 'value_equals'; target: string; value: string }
  | { kind: 'count_equals'; target: string; value: number }
  | { kind: 'url_matches'; pattern: string }
  | { kind: 'freeform'; text: string }
  | { kind: 'number_saved'; target: string; measure: NumberMeasure; name: string }
  | {
      kind: 'number_compare'
      target: string
      measure: NumberMeasure
      op: NumberCompareOp
      /** A name saved by a `number_saved` in an earlier step of the same
       * test. Exactly one of `against` and `value` is set. */
      against?: string
      value?: number
    }

/** How a number check reads its number. Mirror of the server's
 * `NumberMeasure`. `shown` reads the number written in the target
 * ("1,234 results" gives 1234); `elements` counts the elements the
 * target describes, as `count_equals` does. */
export type NumberMeasure = 'shown' | 'elements'

/** How `number_compare` compares. Mirror of the server's
 * `NumberCompareOp`. */
export type NumberCompareOp = 'greater_than' | 'less_than' | 'equals' | 'not_equals'

/** "the number in the results total" / "the count of the result rows".
 * Same wording as the server's `numberSubject`, which the generator reads
 * to tell `shown` from `elements`. */
function numberSubject(measure: NumberMeasure, target: string): string {
  return measure === 'elements' ? `the count of ${target}` : `the number in ${target}`
}

const COMPARE_WORDS: Record<NumberCompareOp, string> = {
  greater_than: 'is more than',
  less_than: 'is less than',
  equals: 'equals',
  not_equals: 'does not equal',
}

/** Render a `TypedAssertion` as a human-readable English clause. Mirror
 * of the server's `renderAssertion` — used by the read-only TestCase
 * display and the text export. */
export function renderAssertion(a: TypedAssertion): string {
  switch (a.kind) {
    case 'visible':
      return a.value ? `${a.target} is visible` : `${a.target} is not visible`
    case 'checked':
      return a.value ? `${a.target} is checked` : `${a.target} is not checked`
    case 'enabled':
      return a.value ? `${a.target} is enabled` : `${a.target} is disabled`
    case 'text_equals':
      return `${a.target} has text "${a.value}"`
    case 'text_contains':
      return `${a.target} contains text "${a.value}"`
    case 'value_equals':
      return `${a.target} has value "${a.value}"`
    case 'count_equals':
      return `${a.target} count is ${a.value}`
    case 'url_matches':
      return `URL matches "${a.pattern}"`
    case 'freeform':
      return a.text
    case 'number_saved':
      return `save ${numberSubject(a.measure, a.target)} as "${a.name}"`
    case 'number_compare':
      return `${numberSubject(a.measure, a.target)} ${COMPARE_WORDS[a.op]} ${a.against !== undefined ? `"${a.against}"` : String(a.value)}`
  }
}

/** MCP step format — used by Playwright MCP integration. `expect`
 * carries typed assertion clauses (#718 slice 1). */
export interface McpStep {
  perform?: string
  expect: TypedAssertion[]
  /** Step shape version (#718 slice 1). Absent on pre-#718 docs. */
  schemaVersion?: number
}

/**
 * TestCase row from the standalone `testcases` collection.
 * Previously embedded in TestPlan.testCases[] — extracted in #84 / Stage B of #75.
 */
export interface PlanTestCase {
  id: string
  planId: string
  applicationId: string
  variantId: string
  name: string
  description: string
  workflow: string
  intent: TestIntent
  categories: PlanCategoryName[]
  priority: TestCasePriority
  status: TestCaseStatus

  /** Structured steps — the single, canonical representation of a
   * test's plan content after #596. Always present; `[]` when the
   * plan hasn't filled it yet (e.g. library-copied fixtures whose
   * source of truth is the spec code). */
  mcpSteps: McpStep[]
  suite?: string
  file?: string
  seedFile?: string

  /** Credential role this test runs as (matches a role in the variant's
   * Test Data credentials). Set by the planner at plan time and editable
   * by the user. Empty/undefined means the test runs unauthenticated. */
  role?: string

  /** Discriminates fixtures from regular tests. Defaults to 'test'.
   * See DESIGN-126-fixtures-as-test-cases.md. */
  type?: 'test' | 'fixture'

  /** TestCase IDs this row depends on. Populated by the planner via
   * role→fixture lookup; user-editable. */
  dependencies?: string[]

  /** The suggestion this test was built from, stamped when
   * the planner adopts one or by "Add as a suggestion". Test Coverage
   * reads this link: a suggestion's status in a variant comes from the
   * tests that carry its id here and their scripts. A test whose link is
   * empty, dangling, or names an archived suggestion counts toward no
   * suggestion. */
  candidateTestId?: string

  /** When this fixture was copy-selected from the system fixture library,
   * the originating SystemFixture id. Stamped at copy time and never
   * modified — UI displays as "originally from <name> v<n>" since the
   * spec may diverge through edits or healer activity. */
  sourceLibraryId?: string
  sourceLibraryVersion?: number
  /** #1941 — set when this auto-matched sign-in helper is behind the catalog
   * but was hand-edited, so the run kept it: the newer version available. */
  signInHelperUpgradeAvailable?: number

  snapshots?: TestCaseSnapshot[]
  testScriptId?: string
  writtenAt?: Date | string
  blockedReason?: string
  blockedAt?: Date | string
  /** Why it was blocked (#1978). */
  blockedKind?: 'sign-in' | 'generation'
  /** Server-derived (#1978): the ONE signal the "Try again" button reads.
   * True only for a sign-in block the operator can re-check. The UI never
   * inspects blockedReason to decide whether to show the button. */
  canRetrySignInBlock?: boolean
  createdAt?: Date | string
  updatedAt?: Date | string
}

export interface PlanPage {
  url: string
  purpose: string
  fingerprint: string
  visited?: boolean
  classification?: 'in-scope' | 'out-of-scope'
  classificationReason?: string
}

export interface PlanWorkflow {
  name: string
  pageUrls: string[]
  description: string
}

export type TestPlanKind = 'smoke' | 'regression' | 'accessibility' | 'exploratory' | 'custom'

export interface TestPlan {
  id: string                   // UUID (see #83 / Stage A of #75)
  applicationId: string
  variantId: string

  name: string
  description?: string
  type?: TestPlanKind

  overview?: string

  pages: PlanPage[]
  visitQueue: string[]
  workflows: PlanWorkflow[]
  fingerprint: string

  /**
   * Test cases live in the standalone `testcases` collection now (#84).
   * Fetch them separately via the test-cases API when needed.
   */

  generatedBy?: 'planner-v1' | 'playwright-mcp'

  createdByRunId: string
  updatedByRunId?: string
  createdAt: Date | string
  updatedAt: Date | string
}

/** User-uploaded reference document attached to an Application
 * (Slice 2 of #857, ie #860). Extracted text feeds the planner
 * prompt as a `## REFERENCE DOCUMENTS` section. The full shape
 * matches the backend's `IReferenceDoc` — `GET /applications/:id`
 * returns the entire array including `text`, so the frontend type
 * carries `text` as a required field. Two consumers:
 *
 *   - **Application config UI** (`Applications.tsx`) — renders the
 *     list rows from this shape; `text` is present in the response
 *     but the list row doesn't show it.
 *   - **Template dialog inheritance hint** — renders only filenames;
 *     `text` is present but unused there too.
 *
 * The `POST /applications/:appId/reference-docs` upload response is
 * a narrower shape (`Omit<ReferenceDoc, 'text'>`) — the route
 * intentionally drops the text from the response since the client
 * already has the file content locally if they need it. See
 * `applicationsAPI.uploadReferenceDoc` in `@/lib/api`. */
/** Status of the candidate-proposer job for a reference doc (Slice
 * 3A of #860). Wire-shape mirror of the backend's `ReferenceDoc
 * ExtractionStatus`. Drives the in-progress pill in
 * `ReferenceDocsSection` and the polling that refreshes the
 * Candidates tab when extraction completes. Optional/undefined on
 * docs uploaded before Slice 3A (no proposer ever touched them);
 * the UI treats `undefined` the same as terminal — no pill,
 * no polling. */
export type ReferenceDocExtractionStatus =
  | 'pending'
  | 'extracting'
  | 'retrying'
  | 'done'
  | 'failed'

/** Why a read failed. Mirror of the backend's `EXTRACTION_FAILURE_KINDS`;
 * the screen words each one. */
export type ExtractionFailureKind =
  | 'no-response'
  | 'busy'
  | 'connection'
  | 'other'
  | 'out-of-credits'
  | 'parts-not-matched'
  | 'text-changed'
  | 'unverified'
  | 'unpaid'
  | 'spending-limit'
  | 'account-refused'

/** What a reference document counts as. Mirror of the backend's
 * `DocumentKind`. Shown to the user as Requirements / Defects /
 * Reference. */
export type DocumentKind = 'requirements' | 'defects' | 'reference'

/** Who decided what a document counts as. `user` — the person picked
 * it; `suggested` — OttoTester's suggestion, not yet confirmed; `none`
 * — nobody has said, so it counts as Reference. */
export type DocumentKindSource = 'user' | 'suggested' | 'none'

/** One part of a reference document. */
export interface ReferenceDocPart {
  id: string
  title: string
  /** ISO time a re-read no longer found this part. */
  missingFromSourceAt?: string
  /** The document's own words this part rests on. Absent on parts saved
   * before quotes existed. */
  quote?: string
  /** A person unticked this part when confirming: it never counts. */
  excluded?: boolean
}

export interface ReferenceDoc {
  id: string
  filename: string
  /** Extracted text — Markdown for `.docx`, flat UTF-8 for `.pdf`,
   * raw content for `.txt` / `.md`. Always present on GET responses;
   * omitted from the upload-response shape (see file header). */
  text: string
  mimeType: string
  /** Extracted-text length in bytes. Surfaced in the UI list. */
  sizeBytes: number
  /** Original uploaded file size in bytes. May differ substantially
   * from `sizeBytes` (a 5MB .docx might extract to 20KB of text). */
  originalSizeBytes: number
  /** SHA-256 of the extracted text. Forward-looking for dedup
   * detection; not surfaced in v1's UI. */
  contentHash: string
  /** How many suggestions this document currently accounts for
   * (#2096). Derived per request from the candidates collection, not
   * stored — it drops when a suggestion is deleted. Counts only the
   * ACTIVE ones, matching what the row's View link opens on. Absent
   * on responses that predate the field; `0` means the document was
   * read and produced nothing, which the row states plainly. */
  candidateCount?: number
  /** Why this document is out of date (#2102). `content` — a Refresh
   * replaced the saved text; `settings` — the kinds of test THIS
   * document suggests were changed. Absent when it is not stale. */
  staleReason?: 'content' | 'settings'
  /** How many suggestions the last read discarded for having a kind
   * this document does not allow (#2102). Absent when no filter was
   * in force; `0` when one was and nothing was dropped. */
  skippedByKindCount?: number
  /** Which kinds of test the proposer may suggest from THIS document or
   * link (#2102 slice 2). Absent means the product default (Positive).
   * Never an empty array; the server refuses one. Set/cleared via
   * `applicationsAPI.setReferenceDocKinds`. */
  proposerIntents?: TestIntent[]
  /** The user's own choice of what this document counts as. Absent
   * until they click an option. Set via
   * `applicationsAPI.setReferenceDocCountsAs`. */
  kind?: DocumentKind
  /** OttoTester's suggestion from the last successful read. Absent when
   * the read gave none, or the document has not been read since the
   * field existed. */
  suggestedKind?: DocumentKind
  /** One-line reason for `suggestedKind`; a suggestion can come without
   * one. */
  suggestedKindReason?: string
  /** What the document counts as, resolved by the server from the three
   * fields above. Read this rather than re-deriving it. Absent on
   * responses that do not attach it (the upload response); treat as
   * Reference with no note. */
  effectiveKind?: DocumentKind
  /** Who decided `effectiveKind`. Drives the note beside the control. */
  kindSource?: DocumentKindSource
  /** The document's parts. Each counts as one requirement (or defect)
   * when the document is marked that way. Absent on a document not given
   * parts yet. */
  parts?: ReferenceDocPart[]
  /** The version of the text `parts` came from; sent back when confirming,
   * so a review of an older list is refused. */
  partsContentHash?: string
  /** ISO time a person confirmed the parts. Until then they count nowhere. */
  partsConfirmedAt?: string
  /** Tag identifying which extractor produced the persisted text
   * (e.g. `mammoth@1.x`, `unpdf@1.x`, `passthrough@1`). Forward-
   * looking for re-extraction comparisons. */
  extractorVersion: string
  /** ISO 8601 string when serialized over the API. */
  uploadedAt: Date | string
  /** User id of the uploader. UI resolves to display name via a
   * separate lookup. */
  uploadedBy: string
  /** Provenance discriminator (Slice 3A of #860). `'user'` for
   * UI-uploaded docs, `'system'` for platform-shipped reference
   * material. UI hides delete/edit affordances on `'system'` docs.
   * Optional/undefined on pre-3A rows (mongoose default is
   * `'user'` so a fresh fetch resolves it; the field can still
   * appear absent on transitional payloads). */
  source?: 'user' | 'system'
  /** Candidate-proposer job state (Slice 3A of #860). Drives the
   * per-row in-progress pill + the polling refresh of the
   * Candidates tab. Undefined on pre-3A docs (the backfill
   * eventually flips them to terminal but legacy responses can
   * still arrive without the field). */
  extractionStatus?: ReferenceDocExtractionStatus
  /** Last successful proposer run timestamp. Surfaced as "extracted
   * X ago" in detail views; primarily a polling-derivative
   * (transition from undefined → set indicates extraction finished). */
  extractedAt?: Date | string
  /** Human-readable failure reason when `extractionStatus === 'failed'`.
   * Capped to ~500 chars upstream. Surfaced inline under a red
   * "Failed" pill when the user expands the row. */
  extractionFailureReason?: string
  /** Why the last read failed, worded on screen. Present with
   * `retrying` and `failed`; absent on failures saved before it existed. */
  extractionFailureKind?: ExtractionFailureKind
  /** When the next try starts. Present only while `retrying`. */
  extractionRetryAt?: Date | string
  /** How many tries were made, when the read ended `failed`. */
  extractionTries?: number
  /** Where a read in rounds has got to. Present while a read runs and
   * after one stopped part-way; cleared when a read finishes. `partIds`
   * is empty while the parts are still being found. */
  readProgress?: {
    partIds: string[]
    partsDone: number
    roundSize: number
  }

  // ── Link-sourced reference docs (#1806) ───────────────────────

  /** How the doc entered the app. `'upload'` (or absent, for pre-#1806
   * rows) — a file was uploaded. `'link'` — the operator pasted a URL
   * and OttoTester saved the page/file's text. Drives the link icon,
   * the provenance line, and the Refresh action on the row. */
  origin?: 'upload' | 'link'
  /** For link docs: the saved link the row displays and Refresh
   * re-reads from. Absent on uploads. */
  sourceUrl?: string
  /** For link docs: when the saved copy was last read from the link.
   * Rendered as "Saved from this link on <date>". ISO 8601 string over
   * the wire. Absent on uploads. */
  fetchedAt?: Date | string
  /** For link docs: true when a Refresh changed the saved text, so the
   * doc's suggestions no longer match. Drives the "Content changed —
   * re-propose to update suggestions" nudge. Cleared when the proposer
   * next runs for the doc. */
  suggestionsStale?: boolean
}

/** Upload-response shape — `text` intentionally absent (see
 * `ReferenceDoc` file header for why). Use this where the API
 * returns ONLY the freshly-uploaded doc's metadata (the
 * `POST .../reference-docs` endpoint). */
export type ReferenceDocMeta = Omit<ReferenceDoc, 'text'>

// ── Candidate tests (Slice 3A–3C of #860) ─────────────────────

/** Aligns with the planner's `tests[].intent` enum so a matched
 * candidate's intent value passes through verbatim. Three kinds since
 * #2102 (validation/error-handling folded into negative). */
export type CandidateTestIntent =
  | 'happy-path'
  | 'negative'
  | 'edge-case'

/** Closed-enum user-action taxonomy. Multi-select per candidate;
 * filterable in the Candidates UI's tag dropdown. Defined as a
 * tuple of all valid values so the UI can iterate without
 * duplicating the list. */
export const CANDIDATE_TEST_TAGS = [
  'authentication',
  'search',
  'create',
  'read',
  'update',
  'delete',
  'validation',
  'navigation',
  'permissions',
] as const
export type CandidateTestTag = (typeof CANDIDATE_TEST_TAGS)[number]

export type CandidateTestSource = 'proposed' | 'user-created'

export interface CandidateStepExpect {
  kind: 'freeform'
  description: string
}

export interface CandidateTestStep {
  perform?: string
  expect: CandidateStepExpect[]
}

/** Client-facing candidate-test row shape. Mirrors `ICandidateTest`
 * on the server minus the embedding vector + Mongoose plumbing.
 * Used by the Candidates UI list + edit dialog. */
/** Scripted-status tier for a single candidate in a given variant.
 *
 * Mirror of the backend's classification at
 * `GET /api/applications/:appId/candidates/scripted-status`. Tiers
 * read as a maturity ladder: a candidate moves left-to-right as the
 * planner adopts it, the scriptwriter drafts code, and the run goes
 * green:
 *
 *   proposed → planned → scripted → ready
 *
 * - **proposed** — no linked TestCase in this variant yet. The
 *   candidate exists in the catalog (proposed from a doc or hand-
 *   authored) but no one's committed it to a plan.
 * - **planned** — TestCase exists, no TestScript yet. Adopted
 *   into a plan; not yet drafted in code.
 * - **scripted** — TestCase + TestScript exist; script not passing
 *   (unknown / failing / blocked). Code is written but unproven.
 * - **ready** — at least one linked TestCase has a passing script.
 *   Safe to replay with confidence.
 *
 * Tiers come from a single round-trip read; consumers use
 * `byCandidate[id]` for the per-row badge and `summary.*` for the
 * variant-scoped rollup the tab badge + summary line show.
 *
 * #974 Slice 4 (#985) renamed the prior 3-tier scheme
 * (unscripted/scripted/verified) into this 4-tier one. No persistent
 * schema migration — tiers are computed at read time. */
export type CandidateScriptedTier = 'proposed' | 'planned' | 'scripted' | 'ready'

/** A single linked TestCase the scripted-status response surfaces
 * to power the detail-pane "Scripted as" card (#974 Slice 4).
 * See `CandidateScriptedStatusResponse.linksByCandidate` for why a
 * candidate can carry more than one of these. */
export interface ScriptedLink {
  /** TestCase id — destination of the `?selected=test-<id>` cross-
   * link on the detail-pane card's "Open in Tests" button. */
  testCaseId: string
  /** The TestCase's name, surfaced verbatim on the card. Source of
   * truth is `TestCase.name`. */
  testCaseName: string
  /** Optional — `undefined` when the planner adopted the TestCase
   * but the scriptwriter hasn't drafted code yet ('planned' tier). */
  testScriptId?: string
  /** Health status of the linked script. `'passing'` is what gates
   * the candidate into the 'ready' tier; other values keep it in
   * 'scripted'. Undefined when no script exists. */
  healthStatus?: string
}

/** Response shape from `applicationsAPI.scriptedStatus(appId, variantId)`.
 * `byCandidate` is the per-row map (keyed by CandidateTest `_id`);
 * `linksByCandidate` is the parallel map of linked TestCases that
 * powers the detail-pane card; `summary` is the per-tier rollup the
 * tab badge and the list summary line both consume.
 *
 * `linksByCandidate` holds a LIST per candidate because one suggestion
 * can legitimately produce more than one test: a planner re-run may
 * write a variation, and the dedup judge (#1786) already removes the
 * ones that are actually the same. */
export interface CandidateScriptedStatusResponse {
  variantId: string
  byCandidate: Record<string, CandidateScriptedTier>
  linksByCandidate: Record<string, ScriptedLink[]>
  /** Pre-run coverage (#1626): for each candidate already covered by an
   * existing test (cosine ≥ the duplicate threshold), the matched test's
   * name + score. Absent key = not covered. Powers the amber
   * "already covered by X" badge in the picker. Optional on the client
   * type so consumers that don't read it (the variant Candidates tab)
   * need not construct it; the server always sends it (best-effort `{}`). */
  coverageByCandidate?: Record<string, { name: string; score: number }>
  summary: {
    total: number
    proposed: number
    planned: number
    scripted: number
    ready: number
  }
  /** Tests in this variant (sign-in helpers excluded) that count toward
   * no active suggestion. Drives the summary line's count and the Active
   * tab's "Show them" filter. Optional on the client type so test
   * fixtures that don't read it need not build it; the server always
   * sends it. */
  uncountedTestIds?: string[]
}

/** One point of the Test Coverage trend (#1442 Slice 4) — one
 * finalized run's snapshot. `capturedAt` is an ISO string. */
export interface CandidateCoverageTrendPoint {
  runId: string
  capturedAt: string
  totalActiveCandidates: number
  authoredCount: number
  passingCount: number
  /** Integer 0-100. */
  authoredPct: number
  passingPct: number
}

export interface CandidateCoverageTrendResponse {
  variantId: string
  /** Oldest-first; empty when no runs fall in the window. */
  points: CandidateCoverageTrendPoint[]
}

/** Requirements/defect coverage read shape, one per dimension inside
 * `VariantCoverage`. Mirrors the server's `IssueCoverageResult`. Rolls up candidate coverage grouped by
 * the linked issue, over the imported-issue population — a source-agnostic
 * view keyed on the linked issue, not a per-tracker engine. */
export type IssueCoverageDimension = 'requirement' | 'defect'

/** The four states an imported issue can be in for one variant. Covered =
 * ANY linked test passes (not ALL). */
export type IssueCoverageState = 'no-test' | 'has-candidate' | 'failing' | 'covered'

interface IssueCoverageRowCommon {
  /** The issue key (`PROJ-140`), or the document's file name. */
  displayKey: string
  /** The issue's title, or the part's title. */
  summary: string
  state: IssueCoverageState
  /** Active proposed tests under this requirement or defect. */
  ideaCount: number
  /** Of those, the ones with a script in this variant. */
  scriptedCount: number
  /** At least one linked script is failing. Tells "Failing" apart from
   * "Scripted, not passing". */
  hasFailingTest: boolean
}

/** A row for an imported issue. `connectionId` + `externalId` find its
 * proposed tests (`linkedConnectionId` / `linkedExternalId`). */
export interface IssueCoverageIssueRow extends IssueCoverageRowCommon {
  source: 'issue'
  externalId: string
  connectionId: string
  /** The tracker's provider slug (`jira`, ...). Shown as the row's
   * source mark. */
  provider: string
}

/** A row for one part of a document marked Requirements or Defects. */
export interface IssueCoverageDocumentRow extends IssueCoverageRowCommon {
  source: 'document'
  documentId: string
  documentFilename: string
  partId: string
  partTitle: string
  /** ISO time a re-read no longer found this part; it was kept because
   * proposed tests still point at it. */
  missingFromSourceAt?: string
}

/** One requirement or defect, from an issue tracker or a document. */
export type IssueCoverageRow = IssueCoverageIssueRow | IssueCoverageDocumentRow

export interface IssueCoverageResult {
  applicationId: string
  variantId: string
  dimension: IssueCoverageDimension
  /** Requirements (or defects) from every source: imported issues of this
   * dimension plus the parts of documents marked that way. A unit with no
   * proposed test still counts, as uncovered. */
  total: number
  coveredCount: number
  /** Integer 0-100, rounded server-side; 0 when total is 0 (never NaN). */
  coveredPct: number
  /** Worst gaps first (no-test, failing, has-candidate, covered). */
  byIssue: IssueCoverageRow[]
  /** Documents of this kind whose requirements (or defects) a person has
   * not confirmed yet; they count nowhere until confirmed. */
  documentsAwaitingReview?: number
}

/** One point of the requirements/defect coverage trend (#1558 Slice 3) —
 * one finalized run's snapshot. `capturedAt` is an ISO string. */
export interface IssueCoverageTrendPoint {
  runId: string
  capturedAt: string
  dimension: IssueCoverageDimension
  total: number
  coveredCount: number
  /** Integer 0-100. */
  coveredPct: number
}

export interface IssueCoverageTrendResponse {
  variantId: string
  dimension: IssueCoverageDimension
  /** Oldest-first; empty when no runs fall in the window. */
  points: IssueCoverageTrendPoint[]
}

/** The proposed-test totals for one variant, inside `VariantCoverage`.
 * Percentages are integers 0-100, rounded server-side; 0 when there are
 * no proposed tests. */
export interface ProposedTestTotals {
  /** Active proposed tests for the app. */
  total: number
  /** Of those, the ones with a script in this variant. */
  scripted: number
  /** Of those, the ones whose script last passed. */
  passing: number
  scriptedPct: number
  passingPct: number
}

/** Every coverage number for one variant, from
 * `GET /applications/:id/coverage?variantId=`. Mirrors the server's
 * `VariantCoverage`. The dashboard cards, their gap lists and the Proposed
 * tab's coverage bar all read this one response. */
export interface VariantCoverage {
  applicationId: string
  variantId: string
  requirements: IssueCoverageResult
  defects: IssueCoverageResult
  proposedTests: ProposedTestTotals
}

export interface CandidateTest {
  _id: string
  workspaceId: string
  applicationId: string
  /** FK to the embedded reference-doc on `Application`. `null` for
   * user-created candidates AND for orphaned candidates whose
   * source ref doc was deleted (with `wasOrphaned: true`). */
  referenceDocId?: string | null
  sourceSectionTitle?: string
  /** The part of the reference document this idea belongs to
   * (`ReferenceDoc.parts[].id`). Absent on hand-added and imported-issue
   * ideas. */
  sourcePartId?: string
  name: string
  description: string
  intent?: CandidateTestIntent
  /** Priority band (#1440), reusing TestCase's `TestCasePriority`. Set by the
   * server; the list comes back ordered P0→P3. Optional only for legacy rows
   * not yet touched by the boot backfill. */
  priority?: TestCasePriority
  preconditions?: string[]
  steps?: CandidateTestStep[]
  tags: CandidateTestTag[]
  source: CandidateTestSource
  active: boolean
  userEdited: boolean
  /** When true, the row carries a `null` `referenceDocId` because
   * its source ref doc was deleted while `userEdited` was true —
   * the row was retained per the cascade rule. UI surfaces a
   * "originally from <deleted doc>" badge based on this flag. */
  wasOrphaned: boolean
  /** ISO time the latest re-read of this suggestion's document (or issue)
   * no longer produced it. Only suggestions a test was built from are kept
   * this way; the UI shows "No longer in the document". Absent otherwise. */
  missingFromSourceAt?: string
  /** Why the last run that tried to write a test from this did not save
   * one: the writer could not see some steps working. `steps` says what
   * it saw instead, one per step. Cleared on edit or a later write. */
  writeProblem?: { reason: string; steps: string[]; runId: string; at: string }
  contentHash: string
  proposerVersion: string
  // ── External issue-tracker provenance (#1558 Slice 2) ──
  /** Provider slug (`jira`, ...) when this candidate came from an external
   * issue rather than a reference doc. Present ⇒ the UI shows issue
   * provenance instead of a reference-doc filename. */
  linkedProvider?: string
  linkedConnectionId?: string
  linkedExternalId?: string
  /** Display key (`PROJ-123`) — the identifier shown in provenance. */
  linkedDisplayKey?: string
  linkedIssueType?: string
  linkedRevisionToken?: string
  linkedIssueSummary?: string
  linkedProjectId?: string
  linkedProjectKey?: string
  createdAt: Date | string
  updatedAt: Date | string
  createdBy?: string
}

export interface Application {
  id: string
  name: string
  description?: string
  icon?: string // Emoji or URL
  /** Embedded generation templates — present on responses from the server
   * until the server completes the RunTemplate consolidation (#1113).
   * After #1113 Slice 0, generation templates are first-class RunTemplate
   * documents in the `runtemplates` collection and this field is removed
   * from the Application doc. UI code that reads this field coalesces with
   * `?? []` so the removal is backwards-compatible without a simultaneous
   * frontend update. The dedicated generation-template CRUD UI (Slice 1 of
   * #1112) will use the new `/run-templates?kind=generation` endpoint. */
  templates?: RunTemplate[]
  /** Canonical, editable application-summary prose (max ~256 chars).
   * Describes WHAT features the app exposes. Auto-promoted from variant
   * scans only when empty here; manual edits win. Threaded into the
   * planner's system prompt only. */
  applicationSummary?: string
  /** Canonical, editable application-domain label (e.g. "trademark
   * clearance search"). Auto-promoted from variant scans only when
   * empty here; manual edits win. */
  applicationDomain?: string
  /** Canonical, editable 200-500 char usage-context prose. Auto-promoted
   * from variant scans only when empty here; manual edits win. */
  applicationUsageContext?: string
  /** App-scoped reference documents (Slice 2 of #857, ie #860).
   * Inline array; the API returns these on the Application doc with
   * the full extracted text. */
  referenceDocs?: ReferenceDoc[]
  /** Count of ACTIVE candidate tests for this app — enriched onto
   * the Application doc by the GET routes so the Applications page's
   * "Candidate tests" tab can render a count badge matching the
   * existing Variants and Reference docs tab badges. Active-only
   * matches the CandidatesSection's default status filter. The full
   * list is still fetched on demand by `<CandidatesSection>` itself
   * when the tab opens — only the count travels on the Application
   * doc. */
  candidateTestCount?: number
  /** The GitHub repository a failed test opens its issue in, as GitHub's
   * numeric repository id. Picked in the app's settings from the
   * repositories the workspace's GitHub Issues connection can see; null or
   * missing means failed tests open no issues. */
  failedTestRepositoryId?: string | null
  /** Per-app GitHub binding (#1154, Slice 2 of meta #1152). Absent
   * when the app hasn't picked a repo yet — the Integrations tab
   * renders the empty-state form per the mockup. The workspace's
   * GitHub App installation is the source of truth for WHICH repos
   * are eligible; this field carries the app's selection within
   * that install. */
  github?: AppGitHubBinding
  /** Per-app Slack binding (#1154). Absent when the app hasn't picked
   * a default channel yet. The workspace's Slack install is the
   * source of truth for WHICH channels are eligible. */
  slack?: AppSlackBinding
  /** Per-app inbound issue-tracker binding (#1558 Slice 1b). Absent
   * until the app is bound to a workspace connection + projects. The
   * workspace connection owns the credentials; this carries only the
   * app's selection. */
  issueSource?: AppIssueSourceBinding
  createdAt: Date
  updatedAt: Date
}

/** Per-app GitHub binding fields (#1154; trimmed in #1184). Mirrors
 * the backend's `IAppGitHubBinding`. Customer-facing label in the
 * Integrations tab reads as plain English ("GitHub repository"); the
 * wire-shape field name keeps the provider prefix for code clarity.
 *
 * `defaultBranch` and `postCheckRunOnFailureDefault` retired in #1184
 * (Slice 1 of meta #1183). See the backend interface for the
 * rationale. */
export interface AppGitHubBinding {
  repoIdentifier?: string
}

/** Slack event types the app default subscribes to (#1154). Variant
 * overrides (Slice 4 of meta #1152) layer on top of this at dispatch
 * time. The wire values match the dispatcher's vocabulary; the form
 * renders plain-English labels. */
export type AppSlackEventSubscription =
  | 'run.completed'
  | 'run.failed'
  | 'run.errored'
  | 'run.cancelled'

export const APP_SLACK_EVENT_SUBSCRIPTIONS: readonly AppSlackEventSubscription[] = [
  'run.completed',
  'run.failed',
  'run.errored',
  'run.cancelled',
] as const

export interface AppSlackBinding {
  defaultChannelId?: string
  defaultEventSubscriptions?: AppSlackEventSubscription[]
}

/** How a provider distinguishes a requirement from a defect — copied
 * from the provider descriptor at bind time. Jira uses `issueType`; a
 * future label-based provider uses `label`. Mirrors the backend's
 * `IssueSourceClassificationScheme`. */
export type IssueSourceClassificationScheme = 'issueType' | 'label'

/** The fixed reasons an automatic issue sync can fail. Mirrors the backend's
 * `ISSUE_SYNC_ERROR_KEYS`. */
export type IssueSyncErrorKey =
  | 'connection_removed'
  | 'sign_in_rejected'
  | 'unreachable'
  | 'bad_filter'
  | 'app_not_installed'
  | 'project_not_visible'
  | 'rate_limited'
  | 'too_many_at_once'
  | 'other'

/** Per-app inbound issue-tracker binding (#1558 Slice 1b). Mirrors the
 * backend's `IAppIssueSourceBinding`. The app selects a workspace
 * connection + one or more of its projects, maps issue types to
 * requirement/defect, and picks the content fields Otto reads. No
 * credentials — only the selection. */
export interface AppIssueSourceBinding {
  connectionId: string
  projectKeys: string[]
  classificationScheme: IssueSourceClassificationScheme
  requirementIssueTypeIds?: string[]
  defectIssueTypeIds?: string[]
  /** Cached `id → name` for the mapped types (survives a Jira rename). */
  issueTypeNames?: Record<string, string>
  /** `label` scheme: the label ids that mean requirement / defect. */
  requirementLabelIds?: string[]
  defectLabelIds?: string[]
  /** Cached `id → name` for the mapped labels; refreshed by each sync. */
  labelNames?: Record<string, string>
  /** Mapped label ids no bound project has any more. Server-managed. */
  missingLabelIds?: string[]
  /** `key → name` for bound projects whose key is a bare id (a repository's
   * number), so screens show `owner/repo`. Server-managed. */
  projectNames?: Record<string, string>
  requirementContentFields?: string[]
  defectContentFields?: string[]
  /** Scope filter in the tracker's filter language (Jira: a JQL fragment) —
   * which issues are in play (#1867). Built by the visual condition builder;
   * empty = every mapped type in the bound projects. */
  scopeFilter?: string
  /** Whether continuous sync polls this binding automatically (#1867).
   * Default off — the operator turns it on. */
  syncEnabled?: boolean
  /** Last successful sync high-water mark, ISO string (#1867). Server-managed
   * (the sync writes it); read-only on the client. */
  lastSyncedAt?: string
  /** Why the last automatic sync failed, or absent/null when it last
   * succeeded. Server-managed; the card turns the key into a fixed
   * plain message. */
  lastSyncError?: { at: string; messageKey: IssueSyncErrorKey; subject?: string; retryAt?: string } | null
  /** Cached `accountId → displayName` for people in `scopeFilter` (#1867 PR2b)
   * — so an assignee/reporter filter shows names on reload. */
  userDisplayNames?: Record<string, string>
}

// ============================================
// Test Cases & Suites
// ============================================

export type ScriptHealthStatus = 'unknown' | 'passing' | 'failing' | 'blocked'

/** Minimal script summary the merge helper consumes. Mirrors the
 * `TestScriptSummary` shape exported from `@/lib/api` — duplicated as
 * a structural type here so `mergeTestCases` doesn't pull a runtime
 * dep on the api module. */
export interface ScriptSummaryForMerge {
  _id: string
  testCaseId: string
  name: string
  scriptCode: string
  healthStatus?: ScriptHealthStatus
  lastPassedAt?: string
  lastFailedAt?: string
  lastExecutedAt?: string
  /** How many fixes have been attempted (#2194). */
  healAttempts?: number
  /** How many of those fixes worked and were saved. */
  healSuccesses?: number
}

/**
 * Join PlanTestCase + TestScriptSummary lists into the frontend-merged
 * `TestCase[]` shape that list displays (TestRunner, TestData,
 * useTestsData) consume. After #596 retired the server's merged
 * `/api/test-cases` endpoint, this client-side merge replaces it —
 * same shape, sourced from two narrower endpoints (`testPlansAPI`
 * for English content, `testScriptsAPI` for script content + health).
 *
 * Plan cases without a matching script (status='planned') still
 * surface — with empty scriptCode + no health fields — so the UI can
 * render the planned-but-not-generated case.
 *
 * `scriptFilename` is derived from `PlanTestCase.file` (basename
 * with `.spec.ts` extension swap). Mirrors what the server's
 * canonicalRelpath emitter produces.
 */
export function mergeTestCases(
  planCases: PlanTestCase[],
  scripts: ScriptSummaryForMerge[],
): TestCase[] {
  const scriptByTcId = new Map<string, ScriptSummaryForMerge>()
  for (const s of scripts) {
    if (s.testCaseId) scriptByTcId.set(s.testCaseId, s)
  }
  return planCases.map(pc => {
    const script = scriptByTcId.get(pc.id)
    return {
      id: pc.id,
      scriptId: script?._id,
      applicationId: pc.applicationId,
      name: pc.name,
      category: pc.categories[0] || 'ui',
      description: pc.description,
      scriptCode: script?.scriptCode ?? '',
      scriptFilename: pc.file ? basenameSwapSpecExt(pc.file) : undefined,
      createdAt: pc.createdAt ?? new Date(0),
      updatedAt: pc.updatedAt ?? new Date(0),
      role: pc.role,
      type: pc.type,
      dependencies: pc.dependencies,
      sourceLibraryId: pc.sourceLibraryId,
      sourceLibraryVersion: pc.sourceLibraryVersion,
      signInHelperUpgradeAvailable: pc.signInHelperUpgradeAvailable,
      healthStatus: script?.healthStatus,
      lastPassedAt: script?.lastPassedAt,
      lastFailedAt: script?.lastFailedAt,
      lastExecutedAt: script?.lastExecutedAt,
      healAttempts: script?.healAttempts,
      healSuccesses: script?.healSuccesses,
    }
  })
}

function basenameSwapSpecExt(file: string): string {
  const normalized = file.replace(/\\/g, '/')
  const idx = normalized.lastIndexOf('/')
  const base = idx >= 0 ? normalized.slice(idx + 1) : normalized
  // Mirror serializers.ts/swapKnownExt: only swap the trailing known
  // extension. A `tc.file` ending in `.yaml` becomes `<base>.spec.ts`;
  // a `.spec.ts` is returned as-is.
  const lower = base.toLowerCase()
  if (lower.endsWith('.spec.ts')) return base
  if (lower.endsWith('.yaml')) return base.slice(0, -'.yaml'.length) + '.spec.ts'
  return base + '.spec.ts'
}

/**
 * Frontend-side merged view of a test case for list displays.
 *
 * After #596 (which retired the server-side merged `/api/test-cases`
 * endpoint) this shape is produced client-side by `mergeTestCases`,
 * which joins `PlanTestCase` (identity + tags + role) with the
 * matching `TestScriptSummary` (script content + health). Surfaces
 * that previously consumed the server's merged shape now consume this
 * derived view via the helper.
 *
 * The shape deliberately omits `steps` / `expectedResult` (ripped in
 * #596 — call sites that need step content read `PlanTestCase.mcpSteps`
 * directly).
 */
export interface TestCase {
  id: string                   // TestCase._id (the logical entity)
  scriptId?: string            // TestScript._id (absent when no script yet)
  applicationId: string
  name: string
  category: string
  description: string
  scriptCode: string
  /** Basename of the canonical `.spec.ts` for this test (e.g.
   * `login.spec.ts`). Shown next to "Playwright spec" in the
   * Test Script pane header. Derived client-side from the test
   * case's `file` field. */
  scriptFilename?: string
  hash?: string
  createdByRunId?: string
  createdAt: Date | string
  updatedAt: Date | string
  /** Credential role this test runs as. See PlanTestCase.role. */
  role?: string
  /** See PlanTestCase.type. */
  type?: 'test' | 'fixture'
  /** See PlanTestCase.dependencies. */
  dependencies?: string[]
  /** See PlanTestCase.sourceLibraryId. */
  sourceLibraryId?: string
  sourceLibraryVersion?: number
  /** See PlanTestCase.signInHelperUpgradeAvailable. */
  signInHelperUpgradeAvailable?: number
  // Health tracking fields
  healthStatus?: ScriptHealthStatus
  lastPassedAt?: Date | string
  lastFailedAt?: Date | string
  lastExecutedAt?: Date | string
  /** How many fixes have been attempted on this test's script (#2194). */
  healAttempts?: number
  /** How many of those fixes worked and were saved. */
  healSuccesses?: number
}

/** Per-member workflow metadata for workflow-mode suites (#936 PR 1).
 * Keyed by `testCaseId` in the parent `TestSuite.workflowMetadata`
 * map.
 *
 * **Order is NOT stored here** — `TestSuite.testCaseIds` array order
 * IS the workflow order. Render-time code derives the 1-based
 * position via `testCaseIds.indexOf(memberId) + 1`. Two storage
 * shapes would drift.
 *
 * `stepLabel` is the workflow-relative role label ("create",
 * "lookup-existing-patent", "approve") — varies per workflow, which
 * is why it lives on the membership rather than the TestCase. */
export interface WorkflowMemberMetadata {
  stepLabel: string
}

export interface TestSuite {
  id: string
  applicationId: string
  variantId: string
  name: string
  description?: string
  testCaseIds: string[]
  /** Marks this suite as a CI/CD entry point. When true, DELETE
   * requires `?force=true` + `X-OttoTester-Confirm-Delete: <name>`
   * header — the UI surfaces a name-typed confirmation modal. See
   * ADR 0015 § referential integrity and #644. */
  ciProtected: boolean
  /** Present only when the suite has been archived (soft-deleted).
   * Listed in the workspace's Archived tab; restorable within the
   * 30-day window; hard-deleted by the sweep job thereafter. */
  archivedAt?: Date

  /** Workflow-mode discriminator (#936 PR 1). When true, this suite
   * represents an ordered business workflow and the UI renders it
   * with per-member ordering + step labels. Absent/false = batch. */
  isWorkflow?: boolean

  /** Failure-cascade behavior for workflow-mode suites (#936 PR 1
   * persists; PR 3 wires runtime). `'continue'` (default) keeps
   * today's parallel-safe semantics; `'skip-rest'` will bundle
   * members into a `describe.serial` spec at generation time. */
  onMemberFailure?: 'continue' | 'skip-rest'

  /** Per-member workflow metadata, keyed by `testCaseId`. Only
   * populated when `isWorkflow === true`. Keys are always a subset
   * of `testCaseIds` after any server-side write. */
  workflowMetadata?: Record<string, WorkflowMemberMetadata>

  createdAt: Date
  updatedAt: Date
}

// ============================================
// Run Template
// ============================================

// `TestCategory` (object of boolean toggles, the per-template input
// filter) was removed in Slice 1 of #857. The Agent Prompt is now the
// sole user-authored steering channel. Per-test output categorization
// still exists as `PlanCategoryName` (see above); see `TestResult.category`.

export type PipelinePhase = 'plan' | 'generate' | 'execute' | 'heal'

/** @deprecated Use PipelinePhase instead */
export type RunMode = PipelinePhase
/** @deprecated Use PipelinePhase instead */
export type ResumePhase = PipelinePhase

/** Log verbosity level for activity log */
export type LogLevel = 'info' | 'debug' | 'trace'

/** Feature-stability expectation (#687) — set via a template's "Feature
 * Status" radio. Drives healer aggressiveness: 'established' repairs
 * brittle tests aggressively; 'volatile' heals conservatively because a
 * failure may be a genuine behavior change. */
export type StabilityExpectation = 'established' | 'volatile'

export interface RunTemplate {
  id: string
  name: string
  description?: string
  phases: PipelinePhase[]
  aiInstructions?: string // LLM hints for this template (e.g., "save takes 2-3 seconds", "uses custom date picker")
  /** Candidate tests the user picked to be created (#1471). Saved on the
   * template; each becomes a test linked back to its candidate (via the
   * UUID, not a name match) so the per-candidate "tests created" count is
   * exact. Generation-kind only; absent on no-pick / legacy templates. */
  pickedCandidateIds?: string[]
  maxTestCases?: number // Stop agent after this many test cases (default 20)
  playwrightTimeout?: number // Per-test timeout in ms for Playwright (default 5000)
  /** Per-assertion polling timeout in ms (#1076). Controls how long
   * `await expect(...)` calls poll before failing. Distinct from
   * `playwrightTimeout` above — that is the per-TEST budget;
   * this is the per-ASSERTION polling budget. Default 5000
   * matches Playwright's own built-in default; pre-#1076
   * templates fetched from the server may not carry the field,
   * so UI sites coalesce `?? 5000`. */
  assertionTimeout?: number
  /** Pause in ms after each test (#1642). Paces back-to-back specs for
   * rate-limited apps. 0 / absent => no pause. */
  interTestDelayMs?: number
  /** Per-action slow-motion in ms (#1642 Slice 2). 0 / absent => falls
   * back to the variant's stored value server-side. */
  slowMotionMs?: number
  isDefault: boolean
  captureTrace?: 'off' | 'on-failure' | 'always'
  logLevel?: LogLevel // Log verbosity: 'info' (default), 'debug' (iterations), 'trace' (full details)
  testDataMode?: 'off' | 'single-row' | 'all-rows' // Test data substitution: off (default), single-row, all-rows
  /** #1800 — "Add input variations" toggle (generation kind). When on, the
   * planner derives each fill field's valid input classes and provisioning
   * seeds one Test Data row per class. Default off. */
  addInputVariations?: boolean
  stabilityExpectation?: StabilityExpectation // Feature Status (#687): biases healer aggressiveness; missing => 'established'
  /** Run-health reporting flags (#1009 of meta #1008). Optional in
   * the frontend type because pre-#1009 templates fetched from the
   * server may not carry the field; UI sites coalesce `?? true` (the
   * server's Mongoose schema-level default does the same on read). */
  reportConsoleErrors?: boolean
  reportNetworkErrors?: boolean
}

export interface ReplayTemplate {
  id: string
  variantId: string
  name: string
  description?: string
  suiteId?: string
  testCaseIds?: string[]
  testDataMode: 'off' | 'single-row' | 'all-rows'
  healOnFailure: boolean
  playwrightTimeout: number
  /** Per-assertion polling timeout in ms (#1076). Controls how long
   * `await expect(...)` calls poll before failing. Same field
   * shape + default (5000) as the matching field on `RunTemplate`
   * so the two surfaces stay symmetric. Optional + coalesce-`?? 5000`
   * because pre-#1076 docs fetched from the server may not carry
   * the field; the Mongoose schema-level default reads back as
   * 5000 on hydration, but lean reads skip defaults so the UI
   * coalesces defensively. */
  assertionTimeout?: number
  /** Pause in ms after each test (#1642). Paces back-to-back specs for
   * rate-limited apps. 0 / absent => no pause. */
  interTestDelayMs?: number
  /** Per-action slow-motion in ms (#1642 Slice 2). 0 / absent => falls
   * back to the variant's stored value server-side. */
  slowMotionMs?: number
  captureTrace: 'off' | 'on-failure' | 'always'
  /** Playwright screenshot capture (ottotester#814). 'off' / 'on-failure' /
   * 'always'. Default 'on-failure' matches the previous hardcoded
   * behavior; legacy templates without the field read as 'on-failure'
   * via the Mongoose schema-level default. */
  captureScreenshot?: 'off' | 'on-failure' | 'always'
  // `headedMode` removed in #994 — the option silently never worked
  // in production (no X server in the Docker image). The field is
  // dropped from the schema; existing docs scrubbed by a boot-time
  // backfill (`backfillDropHeadedMode.ts`).
  logLevel: LogLevel
  createIssueOnFailure: boolean
  /** When true, the terminal hook posts a GitHub Check Run to the linked
   * GitHub integration when a replay run ends in failure (#1115, Slice 2
   * of meta #1112). Only meaningful when a `provider: 'github'`
   * CiIntegration is registered for the workspace AND the triggering run
   * carries `buildMetadata.commitSha` + `buildMetadata.repo`. Default
   * `false`. Optional because pre-#1115 docs fetched from the server will
   * not carry the field; UI sites coalesce `?? false`. */
  postCheckRunOnFailure?: boolean
  // Browser engine the replay runs against (#459). Optional in the
  // frontend type because pre-#459 templates fetched from the server
  // may not carry the field; UI sites coalesce `?? 'chromium'`.
  browser?: 'chromium' | 'firefox' | 'webkit'
  /** Run-health reporting flags (#1009 of meta #1008). Same shape as
   * the matching fields on `RunTemplate` so the two surfaces stay
   * symmetric. Optional + coalesce-`?? true` because pre-#1009 docs
   * fetched from the server may not carry the field; the Mongoose
   * schema-level default reads back as `true` on hydration. */
  reportConsoleErrors?: boolean
  reportNetworkErrors?: boolean
  createdAt: Date | string
  updatedAt: Date | string
}

/** Discriminated kind for the unified RunTemplate model (#1113/#1114). */
export type RunTemplateKind = 'replay' | 'generation' | 'resume'

/** The three ways a run starts (#2175). A run template belongs to
 * exactly one, and only that pool's surfaces list it. Mirrors
 * `RunTemplatePool` on the server. */
export type RunTemplatePool = 'manual' | 'scheduler' | 'ci'

/** Unified RunTemplate shape returned by the `/run-templates` endpoint
 * (all three kinds). Slice 1 (#1114) wires TestRunner to use this
 * instead of the two separate read paths (replayTemplatesAPI.getByVariant
 * + selectedApp.templates). Extends the base document fields with the
 * discriminator `kind` and the soft-orphan flag `wasOrphaned`. Per-kind
 * fields (suiteId, phaseFilter, etc.) are optional since the union covers
 * all three shapes. */
export interface UnifiedRunTemplate {
  id: string
  /** Discriminator key — determines which kind-specific fields are present. */
  kind: RunTemplateKind
  workspaceId: string
  variantId: string | null
  applicationId: string | null
  name: string
  description?: string
  isDefault: boolean
  /** Which way of starting a run owns this template (#2175) — by hand,
   * on a schedule, or from CI. A template belongs to exactly one, so
   * editing it cannot change what the other two do. Optional on the
   * wire only for rows read before the assigning migration ran. */
  pool?: RunTemplatePool
  /** True when the variant-delete cascade has run. variantId is null
   * on orphaned rows. UI surfaces "originally from <deleted variant>." */
  wasOrphaned?: boolean
  // ── Replay-kind fields ──────────────────────────────────────────
  suiteId?: string
  testCaseIds?: string[]
  healOnFailure?: boolean
  captureScreenshot?: 'off' | 'on-failure' | 'always'
  createIssueOnFailure?: boolean
  /** When true, posts a GitHub Check Run on failure (#1115). Replay only. */
  postCheckRunOnFailure?: boolean
  browser?: 'chromium' | 'firefox' | 'webkit'
  // ── Generation-kind fields ──────────────────────────────────────
  aiInstructions?: string
  maxTestCases?: number
  phases?: PipelinePhase[]
  stabilityExpectation?: StabilityExpectation
  /** Candidate tests the user picked (#1471) — generation-kind only. Used
   * on the Start page to tell a runnable template (has a prompt OR picks)
   * from an empty draft, matching the editor's EITHER/OR rule. */
  pickedCandidateIds?: string[]
  /** Ask the planner for input variations (#1800). Generation-kind. */
  addInputVariations?: boolean
  // ── Resume-kind fields ──────────────────────────────────────────
  phaseFilter?: PipelinePhase[]
  // ── Shared fields (all kinds) ───────────────────────────────────
  playwrightTimeout?: number
  assertionTimeout?: number
  /** Pause in ms after each test (#1642) — shared across all kinds. Paces
   * back-to-back specs for rate-limited apps. 0 / absent => no pause. */
  interTestDelayMs?: number
  /** Per-action slow-motion in ms (#1642 Slice 2) — shared across all
   * kinds. 0 / absent => falls back to the variant's stored value. */
  slowMotionMs?: number
  captureTrace?: 'off' | 'on-failure' | 'always'
  logLevel?: LogLevel
  testDataMode?: 'off' | 'single-row' | 'all-rows'
  reportConsoleErrors?: boolean
  reportNetworkErrors?: boolean
  createdAt?: Date | string
  updatedAt?: Date | string
}

/** What a template dialog is allowed to send when it saves (#2199).
 *
 * Every key must be a field a template actually has. The three dialogs
 * used to return `Record<string, unknown>`, which accepts anything —
 * and `resumeMode` / `resumePhases` were sent on every Create Tests save
 * for months, dropped on the way into storage because the schema has no
 * such paths, and read back as defaults. Nothing errored, at any layer.
 *
 * Derived from `UnifiedRunTemplate` rather than restated, so a field
 * added to one kind is sendable without touching this. The identity
 * fields are excluded: they say WHICH template this is, not how the run
 * behaves, and `pool` and `kind` are write-once — the update path strips
 * both. `isDefault` is excluded for the same reason, and because a
 * per-run override must not carry it. */
export type RunTemplateSettings = Partial<
  Omit<
    UnifiedRunTemplate,
    'id' | 'kind' | 'workspaceId' | 'variantId' | 'applicationId' | 'pool' | 'wasOrphaned' | 'isDefault' | 'createdAt' | 'updatedAt'
  >
>

/** Creating a template: its settings, plus the few fields that say WHICH
 * template it is. `kind` and `pool` are write-once — the update path
 * strips both — which is why the patch type below has neither. Accepting
 * them there would let a caller believe a write happened that the server
 * discards, the same shape of lie as #2199. */
export type CreateRunTemplateInput = RunTemplateSettings & {
  kind: RunTemplateKind
  pool?: RunTemplatePool
  variantId?: string | null
  applicationId?: string | null
  isDefault?: boolean
}

/** Editing one. Settings only, plus which template is the default. */
export type UpdateRunTemplateInput = RunTemplateSettings & { isDefault?: boolean }

// ============================================
// Test Run & Results
// ============================================

export interface TestStep {
  id: string
  name: string
  status: TestStatus
  duration?: number // milliseconds
  error?: string
  screenshot?: string // Base64 or URL
  expected?: string
  actual?: string
}

export interface TestResult {
  id: string
  runId: string
  // Per-test output categorization. `PlanCategoryName` (string union)
  // replaced `keyof TestCategory` when the input-side `TestCategory`
  // filter was removed in Slice 1 of #857; the value space is broader
  // (adds 'navigation', 'ui') and matches what the planner actually
  // tags individual test cases with.
  category: PlanCategoryName
  name: string
  status: TestStatus
  steps: TestStep[]
  duration: number
  error?: string
  screenshot?: string
}

export interface GeneratedScript {
  id: string
  testSuiteId: string
  testCaseId?: string
  templateId: string
  code: string // The generated Playwright script
  prompt: string // The prompt used to generate
  createdAt: Date
}

export interface TestRun {
  id: string // UUID — same ID used for directory, WebSocket, and API
  applicationId: string
  variantId: string
  testSuiteId: string
  templateId: string
  additionalInstructions?: string // Run-specific instructions
  generatedScriptId?: string // Reference to the script used
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled'
  results: TestResult[]
  logs: TestLog[] // AI activity logs
  startedAt: Date
  completedAt?: Date
  duration?: number
  summary?: {
    total: number
    passed: number
    failed: number
    skipped: number
  }
  /** #1580 — which phases this run ran, and how many tests it planned /
   * generated. Drive the adaptive run summary: a run that didn't `execute`
   * shows Planned/Generated instead of all-zero execution tiles, on both the
   * live run view and the results page. Absent on runs created before #1580. */
  ranPhases?: Array<'plan' | 'generate' | 'execute' | 'heal'>
  plannedCount?: number
  generatedCount?: number
  /** #1786 — dedup judge outcome + per-suppression audit record for this
   * run. Feeds the run-detail "Skipped as duplicates" card. Absent when the
   * run had no near-duplicate activity. */
  semanticDedup?: {
    escalated: number
    suppressed: number
    rescued: number
    judgeFailures: number
    suppressions: Array<{
      suppressedName: string
      matchedName: string
      score: number
      mode: 'cosine' | 'judge'
      rationale?: string
    }>
  }
  /** Plain-language reason a run FAILED to run its tests (#1589) — a load /
   * compile / runner error, distinct from "tests ran and some failed". Present
   * only on a `failed` run; shown so a broken run isn't read as a clean run. */
  errorReason?: string
  /** Run-level tags — key→value attributes (#747). `release` /
   * `environment` are well-known keys; editable post-hoc via the
   * run-detail tag editor. */
  tags?: Record<string, string>
  /**
   * Trigger attribution metadata set by the trigger dispatcher when a
   * run is fired by a TriggerRule (Option A, ADR 0018). Present on
   * runs whose source is a GitHub (or future provider) webhook event;
   * absent on manually-triggered runs and Option-B CI-YAML runs.
   *
   * Key fields used by the run-history attribution line:
   *   `provider`         — the provider that fired the event ('github' etc.)
   *   `repoIdentifier`   — provider-canonical "owner/repo" string
   *   `branch`           — branch the deploy was for (derived from `deployment.ref`)
   *   `sha`              — commit SHA the run tested (short-forms to 7 chars in UI)
   *   `environment`      — deployment environment label ('Preview', 'Production', custom)
   *   `environmentUrl`   — deploy URL the run targeted (the per-run override)
   *   `triggeredByRuleId`— the TriggerRule _id that matched (may be stale if rule deleted)
   *
   * Pre-#1173 runs fetched from the server may still carry the retired
   * `pullRequestNumber` / `pullRequestUrl` keys via the open index
   * signature below; the TriggerAttribution UI component continues to
   * read them for those legacy rows. New runs only carry the
   * deployment_status keys above.
   *
   * Pre-#1134 runs fetched from the server will not carry this field;
   * UI sites coalesce `?? undefined` and omit the attribution line.
   */
  buildMetadata?: {
    provider?: string
    repoIdentifier?: string
    branch?: string
    sha?: string
    environment?: string
    environmentUrl?: string
    pullRequestNumber?: number
    pullRequestUrl?: string
    triggeredByRuleId?: string
    [key: string]: unknown
  }
}

/**
 * Agent attributed to a log entry. Stamped server-side — most tags
 * from the run's current phase, `auditor` + `rag` via an explicit
 * override for the two agents that run inside another phase (#697).
 * `extractor` is the runtime name of the hint-*extraction* agent — the
 * Activity Log maps it to the "Analyzer" display label; `rag` is the
 * hint-*retrieval* side. `auditor` is the quality judge.
 */
export type LogAgent =
  | 'planner'
  | 'generator'
  | 'executor'
  | 'healer'
  | 'extractor'
  | 'auditor'
  | 'rag'
  | 'system'

export interface TestLog {
  timestamp: Date
  level: 'info' | 'warn' | 'error' | 'debug'
  message: string
  /** Free-form thinking payload (agent reasoning + action list). */
  details?: string
  /** Agent that emitted this entry, stamped from the run phase. */
  agent?: LogAgent
}

// ============================================
// Settings
// ============================================

export interface Settings {
  openai: {
    apiKey?: string
    model: string
    temperature: number
  }
  browser: {
    type: 'chromium' | 'firefox' | 'webkit'
    headless: boolean
    /** Optional override; backend defaults to 1920 when omitted. */
    viewportWidth?: number
    /** Optional override; backend defaults to 1080 when omitted. */
    viewportHeight?: number
    timeout: number
    screenshotOnFailure: boolean
    recordVideo: boolean
    slowMotion: boolean
  }
  theme: 'light' | 'dark' | 'system'
}

// ============================================
// UI State Types
// ============================================

export interface SidebarState {
  isCollapsed: boolean
  recentRuns: Array<{
    id: string
    appName: string
    envName: string
    suiteName: string
  }>
}

// ============================================
// Test reporting (mirrors apps/server/src/application/testReporting/types.ts)
// ============================================
//
// Frontend-side mirror of the dashboard API response shapes (#437).
// Kept in sync manually following the same convention this file uses
// for other server types (TestRun, TestSuite, etc.) — packages/
// shared-types is currently unused so the established pattern is
// duplication, not a workspace import.

/** Discovery (AI-driven exploration) vs replay (suite re-runs). */
export type RunType = 'discovery' | 'replay'

/** Reduced status per TestExecution row. */
export type TestExecutionStatus =
  | 'passed'
  | 'failed'
  | 'skipped'
  | 'timedOut'
  | 'interrupted'

/** Error categorization buckets emitted by the Playwright failure
 * categorizer. `'uncategorized'` is the synthetic bucket the server's
 * `failureSummary.ts` (UNCATEGORIZED constant) emits for failed tests
 * with no recorded `errorType` — distinct from `'unknown'`, which the
 * categorizer assigns when it inspected the error but couldn't fit it
 * into a known bucket. */
export type ErrorType =
  | 'selector-not-found'
  | 'timeout'
  | 'assertion-failure'
  | 'navigation-error'
  | 'network-error'
  | 'unknown'
  | 'uncategorized'

/** Server-side run-type filter. `'all'` is the operator escape hatch. */
export type RunTypeFilter = RunType | 'all'

// ── /trends ─────────────────────────────────────────────────────────

export interface TrendsRunEntry {
  runId: string
  createdAt: string
  /** Run-level tags (#747) — the map the run carries; `{}` when
   * untagged. Keys are what the user set (`release`, …). */
  tags: Record<string, string>
  runType: RunType
  totalTests: number
  passed: number
  failed: number
  skipped: number
  flaky: number
  durationMs: number
  /** `passed / totalTests`, in `[0, 1]`. */
  passRate: number
}

export interface TrendsResponse {
  variantId: string
  windowStart: string
  windowEnd: string
  runType: RunTypeFilter
  /** Newest-first. */
  runs: TrendsRunEntry[]
}

// ── /flaky ──────────────────────────────────────────────────────────

export interface FlakyTestEntry {
  testName: string
  failureRate: number
  failures: number
  runs: number
  lastFailedAt: string
}

export interface FlakyTestsResponse {
  variantId: string
  windowRuns: number
  runType: RunTypeFilter
  flakyTests: FlakyTestEntry[]
}

// ── /regression-delta ───────────────────────────────────────────────

export interface NewlyFailingTest {
  testName: string
  currentStatus: TestExecutionStatus
  errorType?: string
  priorStatus: 'passed'
}

export interface NewlyPassingTest {
  testName: string
  currentStatus: 'passed'
  priorStatus: TestExecutionStatus
  priorErrorType?: string
}

export interface PersistentlyFailingTest {
  testName: string
  errorType?: string
  runsFailedInARow: number
}

export interface FlippedTest {
  testName: string
  currentStatus: TestExecutionStatus
  priorStatus: TestExecutionStatus
}

export interface RegressionDeltaResponse {
  runId: string
  runType: RunType
  priorRunId: string | null
  newlyFailing: NewlyFailingTest[]
  newlyPassing: NewlyPassingTest[]
  persistentlyFailing: PersistentlyFailingTest[]
  flipped: FlippedTest[]
}

/** The 400-response surface for discovery runs. The frontend's
 * useRegressionDelta hook converts the 400 into this typed value so
 * components can branch on `notApplicable` without try/catch. */
export interface RegressionDeltaNotApplicable {
  notApplicable: true
  /** Human-readable reason returned by the server. */
  reason: string
  runType?: RunType
}

// ── /failure-summary ────────────────────────────────────────────────

export interface FailureSummaryTest {
  testName: string
  errorMessage: string
}

export interface FailureSummaryCategory {
  errorType: string
  count: number
  tests: FailureSummaryTest[]
  truncated: boolean
}

export interface FailureSummaryResponse {
  runId: string
  runType: RunType
  totalFailures: number
  byCategory: FailureSummaryCategory[]
}

// ── /triage (#443) ──────────────────────────────────────────────────

export interface TriageEntry {
  testCaseId: string | null
  testScriptId?: string
  testName: string
  testFile?: string
  identityFallback?: 'name'
  status: TestExecutionStatus
  errorType?: ErrorType
  errorPreview?: string
  firstFailedRunIndex?: number
  firstFailedRunId?: string
  streakLength: number
  last12Outcomes: ('pass' | 'fail' | 'skip')[]
  /** RunIds aligned 1:1 with `last12Outcomes` — same length, same
   * order, newest-last. The sparkline uses these to make each cell
   * deep-link to the run it represents. */
  last12RunIds: string[]
  flakeRate?: number
  scriptRewrites?: number
}

export interface TriageBuckets {
  newFailures: TriageEntry[]
  persistentFailures: TriageEntry[]
  flaky: TriageEntry[]
  skipped: TriageEntry[]
  resolved: TriageEntry[]
  all: TriageEntry[]
}

export type TriageBucketKey = keyof TriageBuckets

export interface TriageBucketMeta {
  total: number
  returned: number
  nextCursor?: string
}

export interface TriageResponse {
  variantId: string
  windowMs: number
  runType: RunTypeFilter
  runsConsidered: number
  latestRunId: string | null
  buckets: TriageBuckets
  bucketMeta: Record<TriageBucketKey, TriageBucketMeta>
}

// ── /comparison (#747) ──────────────────────────────────────────────
// Hand-kept mirror of the server's `application/testReporting/types.ts`
// comparison shapes — same convention as the reporting types above.

/** The axis the two compared buckets differ on. */
export type ComparisonAxis = 'release' | 'date'

/** The six mutually-exclusive diff classes. */
export type ComparisonClass =
  | 'regressions'
  | 'fixes'
  | 'stablePass'
  | 'stableFail'
  | 'added'
  | 'removed'

/** One test's row in a comparison diff class. Identity is `testScriptId`. */
export interface ComparisonTestRow {
  testScriptId: string
  testName: string
  /** Last-run-wins status in bucket A; `null` when absent from A. */
  aStatus: TestExecutionStatus | null
  /** Last-run-wins status in bucket B; `null` when absent from B. */
  bStatus: TestExecutionStatus | null
  /** Observed both passing and failing across bucket A's runs. */
  flakeA: boolean
  /** Observed both passing and failing across bucket B's runs. */
  flakeB: boolean
}

/** Resolved run-set metadata for one comparison bucket. */
export interface ComparisonBucketMeta {
  /** `release` axis — the `tags.release` value. */
  release?: string
  /** `date` axis — the ISO window requested. */
  windowFrom?: string
  windowTo?: string
  runCount: number
  testCount: number
  /** True when more runs matched than `limit` (oldest dropped). */
  capped: boolean
  limit: number
  oldestIncludedAt: string
  newestIncludedAt: string
}

export interface ComparisonResponse {
  variantId: string
  axis: ComparisonAxis
  /** Suite filter applied to both buckets, or `null` when unfiltered. */
  suiteId: string | null
  /** Bucket A — the baseline / "from" side. */
  a: ComparisonBucketMeta
  /** Bucket B — the target / "to" side. */
  b: ComparisonBucketMeta
  /** Exact (uncapped) test count per class. */
  counts: Record<ComparisonClass, number>
  /** Per-class rows, capped server-side per class. */
  rows: Record<ComparisonClass, ComparisonTestRow[]>
}

// ============================================
// TriggerRule — CI provider auto-trigger bindings (ADR 0018, #1132)
// ============================================
//
// Discriminated union mirroring the backend discriminator model.
// Provider-agnostic base + per-provider sub-types. Today only GitHub
// is populated; GitLab/Bitbucket/AzureDevOps land as additive entries.
// Frontend components branch on `rule.provider`.

/** Providers the TriggerRule discriminator supports. v1 ships 'github'. */
export type TriggerRuleProvider = 'github' | 'gitlab' | 'azureDevOps' | 'bitbucket'

/** GitHub event types (stored on the GitHub sub-schema).
 *
 * `'deployment_status'` is the sole supported event post-#1173/#1174
 * (meta #1172). The legacy `'push' | 'pull_request'` transitional union
 * members shipped in Slice 1 to keep the editor / list typechecking
 * during the slice 1→2 transition; Slice 2 (#1174) removes them now
 * that the editor + list have been rewritten to match. The seam is
 * retained as a single-literal type so a future second GitHub event
 * type lands as an additive union edit, not a discriminator-collapse
 * migration. */
export type GitHubEventType = 'deployment_status'

/** Base fields shared across every provider's trigger rule. */
export interface TriggerRuleBase {
  id: string
  workspaceId: string
  provider: TriggerRuleProvider
  integrationId: string
  /** Provider-canonical repo identifier (e.g. "owner/repo" for GitHub). */
  repoIdentifier: string
  /** Optional environment filter (#1173). Matches the deployment's
   * `environment` label exactly (`"Preview"` / `"Production"` /
   * custom). Empty / absent = match any environment. */
  environment?: string
  /** FK to the RunTemplate that fires when this rule matches.
   * Only Replay templates may be linked (Q7, ADR 0018). */
  templateId: string
  /** Set on auto-created rules. UI badges them as "Default". */
  isDefault?: boolean
  createdAt: string
  updatedAt: string
}

/** GitHub-specific TriggerRule. */
export interface GitHubTriggerRule extends TriggerRuleBase {
  provider: 'github'
  eventType: GitHubEventType
}

/** Discriminated union of all provider-specific rule shapes. */
export type TriggerRule = GitHubTriggerRule
// Future: | GitLabTriggerRule | AzureDevOpsTriggerRule | BitbucketTriggerRule
